import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Share2, Lock, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { localDate } from "@/lib/dates";
import { generateLightCardImage } from "@/lib/lightCardImage";
import { SuitInsight } from "./SuitInsight";
import { CardHistory } from "./CardHistory";
import { SuitCollection } from "./SuitCollection";

export const LightDeck = ({ token, onMilestone, palette }) => {
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [daily, setDaily] = useState(null);
  const [bonusCards, setBonusCards] = useState([]);
  const [weeklyCard, setWeeklyCard] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [flipped, setFlipped] = useState({});
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/lightdeck/cards`, headers).then(async (r) => {
      setDeck(r.data);
      if (!r.data.teaser) {
        const d = await axios.get(`${API}/lightdeck/daily?date=${localDate()}`, headers);
        setDaily(d.data.card);
        setBonusCards(d.data.bonus_cards || []);
      } else {
        const w = await axios.get(`${API}/lightdeck/weekly-teaser?date=${localDate()}`, headers);
        setWeeklyCard(w.data.card);
      }
    }).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const draw = async () => {
    setDrawing(true);
    try {
      const res = await axios.post(`${API}/lightdeck/draw`, { client_date: localDate() }, headers);
      if (res.data.bonus) {
        setBonusCards((p) => [...p, res.data.card]);
        toast.success("Another card found you ✨");
      } else {
        setDaily(res.data.card);
        if (res.data.garden?.points_earned) {
          toast.success(`Your light for today ✨ +${res.data.garden.points_earned} garden points`);
        }
        if (res.data.milestone) onMilestone(res.data.milestone);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't draw a card");
    } finally {
      setDrawing(false);
    }
  };

  const share = async (card) => {
    try {
      const blob = await generateLightCardImage(card);
      const file = new File([blob], "be-the-light.png", { type: "image/png" });
      const text = `Today's light: "${card.front}" — ${card.back} 🌼 Be The Light · House of Bloom ${window.location.origin}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text, title: "Be The Light" });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "be-the-light.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded — ready to post 🌼");
    } catch (e) {
      toast.error("Couldn't create the card image");
    }
  };

  if (!deck) return null;

  if (deck.teaser) {
    return (
      <div className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(130deg, #4A6B5D, #0F766E)" }} data-testid="light-deck-teaser">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5" style={{ color: "#FCD34D" }} />
          <h2 className="text-lg font-bold font-body">Be The Light — Card Deck</h2>
        </div>
        <p className="text-xs opacity-85 mb-4">A {deck.total}-card deck of daily alignments. Draw one card each morning and let it shape your day.</p>
        {weeklyCard && (
          <div className="rounded-2xl p-5 text-center mb-4" style={{ backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid #FCD34D66" }} data-testid="weekly-teaser-card">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2" style={{ color: "#FCD34D" }}>Your free card this week</p>
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">{weeklyCard.suit}</p>
            <h3 className="text-xl font-bold my-1.5" style={{ fontFamily: "Georgia, serif" }}>{weeklyCard.front}</h3>
            <p className="text-xs opacity-90 italic leading-relaxed">{weeklyCard.back}</p>
          </div>
        )}
        <div className="flex gap-2 mb-4">
          {deck.cards.map((c) => (
            <div key={c.id} className="flex-1 rounded-xl border border-white/25 bg-white/10 p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest opacity-70">{c.suit}</p>
              <p className="text-sm font-bold mt-1">{c.front}</p>
              <Lock className="w-3.5 h-3.5 mx-auto mt-2 opacity-60" />
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate("/subscription")}
          className="w-full rounded-xl py-3 text-sm font-bold"
          style={{ backgroundColor: "#D97706", color: "#FFFFFF" }}
          data-testid="light-deck-upgrade-btn"
        >
          Draw a new card every day with Premium ✨
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border bg-white p-5" style={{ borderColor: palette.border }} data-testid="light-deck">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5" style={{ color: "#D97706" }} />
        <h2 className="text-lg font-bold font-body" style={{ color: palette.primary }}>Be The Light</h2>
      </div>
      <p className="text-xs opacity-70 mb-4">{daily ? "Today's card — carry it with you" : "Draw your card for today"}</p>

      {!daily ? (
        <button
          onClick={draw}
          disabled={drawing}
          className="w-full rounded-2xl py-10 text-center transition-transform active:scale-95"
          style={{ background: "linear-gradient(135deg, #0F766E, #4A6B5D)", opacity: drawing ? 0.7 : 1 }}
          data-testid="light-deck-draw-btn"
        >
          <div className="w-16 h-24 mx-auto rounded-xl border-2 flex items-center justify-center animate-pulse" style={{ borderColor: "#FCD34D", backgroundColor: "#0d5f59" }}>
            <Sparkles className="w-6 h-6" style={{ color: "#FCD34D" }} />
          </div>
          <p className="text-white text-sm font-bold mt-3">{drawing ? "Drawing…" : "Tap to draw today's card"}</p>
        </button>
      ) : (
        <>
          <div className="rounded-2xl p-6 text-center text-white" style={{ background: "linear-gradient(135deg, #0F766E, #4A6B5D)", boxShadow: "0 0 0 3px #FCD34D33" }} data-testid="light-deck-daily-card">
            <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FCD34D" }}>{daily.suit}</p>
            <h3 className="text-2xl font-bold my-2" style={{ fontFamily: "Georgia, serif" }}>{daily.front}</h3>
            <p className="text-sm opacity-90 italic leading-relaxed">{daily.back}</p>
            <button
              onClick={() => share(daily)}
              className="mt-4 px-5 py-2 rounded-full text-xs font-bold inline-flex items-center gap-1.5"
              style={{ backgroundColor: "#D97706" }}
              data-testid="light-card-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> Share this card
            </button>
          </div>

          {bonusCards.map((card, i) => (
            <div key={`${card.id}-${i}`} className="rounded-2xl p-5 text-center text-white mt-3" style={{ background: "linear-gradient(135deg, #4A6B5D, #5d7c6e)" }} data-testid={`light-deck-bonus-card-${i}`}>
              <p className="text-[9px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#FCD34D" }}>Deeper draw · {card.suit}</p>
              <h4 className="text-lg font-bold mb-1" style={{ fontFamily: "Georgia, serif" }}>{card.front}</h4>
              <p className="text-xs opacity-90 italic leading-relaxed">{card.back}</p>
              <button
                onClick={() => share(card)}
                className="mt-3 px-4 py-1.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1"
                style={{ backgroundColor: "#D9770699", color: "#FFF" }}
                data-testid={`bonus-card-share-btn-${i}`}
              >
                <Share2 className="w-3 h-3" /> Share
              </button>
            </div>
          ))}

          {bonusCards.length < 3 && (
            <button
              onClick={draw}
              disabled={drawing}
              className="w-full mt-3 rounded-xl border-2 border-dashed py-2.5 text-xs font-bold"
              style={{ borderColor: `${palette.primary}45`, color: palette.primary, opacity: drawing ? 0.6 : 1 }}
              data-testid="light-deck-bonus-draw-btn"
            >
              {drawing ? "Drawing…" : "Called to go deeper? Draw another card"}
            </button>
          )}
        </>
      )}

      <SuitInsight token={token} palette={palette} />

      <button
        onClick={() => setBrowsing(!browsing)}
        className="w-full mt-4 text-xs font-bold flex items-center justify-center gap-1"
        style={{ color: palette.primary }}
        data-testid="light-deck-browse-btn"
      >
        {browsing ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {browsing ? "Close the deck" : `Browse the full deck (${deck.total} cards)`}
      </button>

      {browsing && (
        <div className="grid grid-cols-2 gap-3 mt-4" data-testid="light-deck-grid">
          {deck.cards.map((card) => {
            const isFlipped = flipped[card.id];
            return (
              <button
                key={card.id}
                onClick={() => setFlipped((p) => ({ ...p, [card.id]: !p[card.id] }))}
                className="rounded-2xl border p-4 min-h-[120px] text-left transition-all duration-300 active:scale-95"
                style={{
                  borderColor: isFlipped ? palette.primary : palette.border,
                  backgroundColor: isFlipped ? palette.primary : "#FFFFFF",
                  color: isFlipped ? "#FFFFFF" : palette.text,
                }}
                data-testid={`light-card-${card.id}`}
              >
                {isFlipped ? (
                  <p className="text-xs leading-relaxed">{card.back}</p>
                ) : (
                  <>
                    <p className="text-[10px] uppercase tracking-widest opacity-60">{card.suit}</p>
                    <p className="font-bold text-sm mt-1">{card.front}</p>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      <SuitCollection token={token} palette={palette} refreshKey={(daily ? 1 : 0) + bonusCards.length} />

      <CardHistory token={token} palette={palette} />
    </div>
  );
};
